import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Button from '../../reusable/Button/Button';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import { toast } from '../../reusable/Toast/toastStore';
import { useGetPublishedCouponsQuery } from '../../store/api/couponsApi';
import { t } from '../../i18n/i18n';
import '../../reusable/InfoPanel/InfoPanel.scss';
import './Coupons.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * Codes are few — three today — so the page asks for them in one go and sorts
 * and filters them here. Filtering a single page of a paged list would hide
 * codes that sit on the next one; the pager below only appears if the back end
 * ever publishes more than this.
 */
const PAGE_SIZE = 100;

/** The last stretch of a code's window, when "Ends in" turns to a warning. */
const ENDING_SOON = 3 * 86400000;

const DAY = 86400000;
const HOUR = 3600000;
const MINUTE = 60000;

/**
 * A coupon's start or end as an instant. The row carries a plain date and a
 * plain time with no offset, which the live page reads as the viewer's local
 * time — kept the same here, so both dashboards agree on when a code runs out.
 * A missing time is the whole day: the start of it, or the end of it.
 */
const instantOf = (date, time, endOfDay) => {
  if (!date) return NaN;
  return Date.parse(`${date}T${time || (endOfDay ? '23:59:59' : '00:00:00')}`);
};

const startOf = (c) => instantOf(c.start_date, c.start_time, false);

/**
 * When a code runs out: `expires_at`, a full timestamp with its zone, so
 * Valid until and Ends in are the same instant wherever the trader is.
 */
const endOf = (c) => Date.parse(c.expires_at ?? '');

/** Upcoming, active or expired — the same window test the live page runs. */
const windowOf = (coupon, now) => {
  const end = endOf(coupon);
  const start = startOf(coupon);
  if (Number.isFinite(end) && now > end) return 'expired';
  if (Number.isFinite(start) && now < start) return 'upcoming';
  return 'active';
};

const WINDOW_CHIP = {
  active: { label: 'Active', tone: 'success' },
  upcoming: { label: 'Starts soon', tone: 'info' },
  expired: { label: 'Expired', tone: 'neutral' },
};

/** Active first, then the ones still to open, then the spent ones. */
const WINDOW_ORDER = { active: 0, upcoming: 1, expired: 2 };

/**
 * The offer, split for the headline: the figure is drawn large and the unit
 * small beside it. `kind` picks the card's accent — a percentage, a fixed
 * amount, or a deal that is neither (10KFOR40 carries 0 in both fields and
 * says what it is in its description).
 */
const offerOf = (coupon) => {
  const percent = Number(coupon.coupon_percent);
  if (percent > 0) return { kind: 'percent', figure: `${percent}%`, unit: 'off' };
  const amount = Number(coupon.coupon_amount);
  if (amount > 0) return { kind: 'amount', figure: `$${amount.toLocaleString('en-US')}`, unit: 'off' };
  return { kind: 'deal', figure: 'Special', unit: 'offer' };
};

const dateTimeOf = (at, withTime, timeZone) => {
  if (!Number.isFinite(at)) return '—';
  const d = new Date(at);
  const day = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone });
  return withTime
    ? `${day}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone })}`
    : day;
};

/** "25d 4h", "6h 12m", "8m" — two units at most, the largest first. */
const spanOf = (ms) => {
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / HOUR);
  const m = Math.max(1, Math.floor((ms % HOUR) / MINUTE));
  if (d > 0) return t('{{d}}d {{h}}h', { d, h });
  if (h > 0) return t('{{h}}h {{m}}m', { h, m });
  return t('{{m}}m', { m });
};

/** How many challenge names a card lists before folding the rest away. */
const CHALLENGES_SHOWN = 3;

function CodeStrip({ code, disabled }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(id);
  }, [copied]);

  return (
    <div className={`coupon-code${disabled ? ' is-muted' : ''}`}>
      <span className="coupon-code__value">{code}</span>
      <CopyToClipboard
        text={code}
        onCopy={(_text, ok) => {
          if (ok) {
            setCopied(true);
            toast.success(t('Coupon code copied'));
          } else {
            toast.error(t('Could not copy the code'));
          }
        }}
      >
        <Button
          variant="pill"
          height={32}
          paddingInline={12}
          className={`coupon-code__btn${copied ? ' is-copied' : ''}`}
          aria-label={
            copied
              ? t('Coupon code {{code}} copied', { code })
              : t('Copy coupon code {{code}}', { code })
          }
        >
          <span
            className="icon coupon-code__icon"
            style={icon(copied ? 'check-sm' : 'copy')}
            aria-hidden="true"
          />
          <span aria-live="polite">{copied ? t('Copied') : t('Copy code')}</span>
        </Button>
      </CopyToClipboard>

      {/* A route change rather than a link: react-router swaps the view in
          place, so the checkout arrives without the white flash of a reload.
          The code rides in the query string — see TradingAssessment, which
          seeds the Discount Code field from it and then tidies the URL. */}
      {/* <Button
        variant="light"
        height={32}
        paddingInline={14}
        className="coupon-code__use"
        disabled={disabled}
        onClick={() => navigate(`/trading-assessment?coupon=${encodeURIComponent(code)}`)}
        aria-label={`Use coupon ${code} at checkout`}
      >
        Use coupon
        <span className="icon coupon-code__arrow" style={icon('arrow-right')} aria-hidden="true" />
      </Button> */}
    </div>
  );
}

function CouponCard({ coupon, now }) {
  const [showAll, setShowAll] = useState(false);

  const state = windowOf(coupon, now);
  const chip = WINDOW_CHIP[state];
  const offer = offerOf(coupon);
  const challenges = coupon.challenges ?? [];
  const code = coupon.coupon_name;
  const end = endOf(coupon);
  const left = end - now;
  const endingSoon = state === 'active' && left > 0 && left <= ENDING_SOON;

  const shown = showAll ? challenges : challenges.slice(0, CHALLENGES_SHOWN);
  const hidden = challenges.length - shown.length;

  return (
    <article
      className={`coupon-card coupon-card--${offer.kind}${state !== 'active' ? ` coupon-card--${state}` : ''}`}
      aria-label={`${code}: ${t(offer.figure)} ${t(offer.unit)}`}
    >
      {/* ---- the offer ---- */}
      <div className="coupon-card__offer">
        <div className="coupon-card__top">
          <span className="coupon-card__headline">
            <span className="coupon-card__figure">{t(offer.figure)}</span>
            <span className="coupon-card__unit">{t(offer.unit)}</span>
          </span>
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        </div>
        {coupon.description && <p className="coupon-card__desc">{coupon.description}</p>}
      </div>

      {/* The tear line: a dashed rule between two notches cut into the card's
          sides, so the code below reads as the stub you take away. */}
      <div className="coupon-card__tear" aria-hidden="true" />

      {/* ---- the stub ---- */}
      <div className="coupon-card__stub">
        <CodeStrip code={code} disabled={state === 'expired'} />

        <dl className="coupon-card__rows">
          <div className="coupon-card__row">
            <dt className="coupon-card__row-label">
              {state === 'upcoming' ? t('Opens') : state === 'expired' ? t('Ended') : t('Valid until')}
            </dt>
            <dd className="coupon-card__row-value">
              {/* The end as `expires_at` states it ("…T23:59:59Z" reads 23:59),
                  not moved into the viewer's zone. */}
              {state === 'upcoming'
                ? dateTimeOf(startOf(coupon), true)
                : dateTimeOf(end, true, 'UTC')}
            </dd>
          </div>

          {state !== 'expired' && (
            <div className="coupon-card__row">
              <dt className="coupon-card__row-label">
                {state === 'upcoming' ? t('Opens in') : t('Ends in')}
              </dt>
              <dd
                className={`coupon-card__row-value coupon-card__countdown${endingSoon ? ' is-soon' : ''}`}
              >
                {endingSoon && <span className="coupon-card__pulse" aria-hidden="true" />}
                {state === 'upcoming'
                  ? spanOf(startOf(coupon) - now)
                  : Number.isFinite(left)
                    ? spanOf(left)
                    : t('No end date')}
              </dd>
            </div>
          )}

          <div className="coupon-card__row">
            {/* `multi_use` — whether the code can be redeemed more than once. */}
            <dt className="coupon-card__row-label">{t('Uses')}</dt>
            <dd className="coupon-card__row-value">
              {coupon.multi_use === false ? t('Single use') : t('Reusable')}
            </dd>
          </div>

          <div className="coupon-card__row">
            <dt className="coupon-card__row-label">{t('Applies to')}</dt>
            <dd className="coupon-card__row-value">
              {challenges.length === 0
                ? t('Every challenge')
                : challenges.length === 1
                  ? t('{{n}} challenge', { n: challenges.length })
                  : t('{{n}} challenges', { n: challenges.length })}
            </dd>
          </div>
        </dl>

        {challenges.length > 0 && (
          <ul className="coupon-card__challenges" aria-label={t('Challenges this code applies to')}>
            {shown.map((c) => (
              <li key={c.id ?? c.name} className="coupon-card__challenge">
                {c.name}
              </li>
            ))}
            {challenges.length > CHALLENGES_SHOWN && (
              <li>
                <button
                  type="button"
                  className="coupon-card__challenge coupon-card__more"
                  aria-expanded={showAll}
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? t('Show less') : t('+{{n}} more', { n: hidden })}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </article>
  );
}

function CouponsSkeleton() {
  return (
    <div className="coupon-grid" aria-hidden="true">
      {Array.from({ length: 3 }, (_, i) => (
        <div className="coupon-card" key={i}>
          <div className="coupon-card__offer">
            <div className="coupon-card__top">
              <Skeleton w={120} h={34} />
              <Skeleton w={56} h={22} pill />
            </div>
            <Skeleton w="75%" h={12} />
          </div>
          <div className="coupon-card__tear" />
          <div className="coupon-card__stub">
            <Skeleton w="100%" h={44} radius={8} />
            <Skeleton w="100%" h={12} />
            <Skeleton w="100%" h={12} />
            <Skeleton w="100%" h={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Coupons — N02. The live dashboard's page is drawn by hand: each code has its
 * own artwork, matched on the code's name, so a new code shows up as a blank
 * header until someone designs one. Here every card is generated from the row
 * itself — the offer, the code, its window and the challenges it covers — so a
 * code the back end publishes is on the page as soon as it exists.
 */
export default function Coupons() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, refetch } = useGetPublishedCouponsQuery({
    page,
    page_size: PAGE_SIZE,
  });

  // The countdowns read to the minute, so the page re-reads the clock on the
  // minute rather than every second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  /**
   * Active codes lead, the one running out soonest first — that is the one a
   * trader should act on — then the ones still to open, by opening, then the
   * spent ones, most recently ended first.
   */
  const sorted = useMemo(() => {
    const rows = (data?.results ?? []).map((c) => ({ coupon: c, state: windowOf(c, now) }));
    return rows.sort((a, b) => {
      const byWindow = WINDOW_ORDER[a.state] - WINDOW_ORDER[b.state];
      if (byWindow) return byWindow;
      if (a.state === 'upcoming') return startOf(a.coupon) - startOf(b.coupon);
      if (a.state === 'expired') return endOf(b.coupon) - endOf(a.coupon);
      return (endOf(a.coupon) || Infinity) - (endOf(b.coupon) || Infinity);
    });
  }, [data, now]);

  const visible = sorted.filter((r) => r.state === 'active');

  const pageCount = Math.max(1, Math.ceil((data?.count ?? 0) / PAGE_SIZE));

  return (
    <div className="coupons">
      <header className="coupons__head">
        <div className="page-head">
          <h1 className="page-head__title">{t('Coupons')}</h1>
          <p className="page-head__meta">
            {t('Codes you can use at checkout. Press Copy code and paste it into the Discount Code field.')}
          </p>
        </div>

      </header>

      {isLoading ? (
        <CouponsSkeleton />
      ) : isError ? (
        <div className="coupon-empty">
          <span className="icon coupon-empty__icon" style={icon('nav-coupons')} aria-hidden="true" />
          <span className="coupon-empty__title">{t('We could not load the coupons.')}</span>
          <Button variant="toolbar" onClick={refetch}>
            {t('Try again')}
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <div className="coupon-empty">
          <span className="icon coupon-empty__icon" style={icon('nav-coupons')} aria-hidden="true" />
          <span className="coupon-empty__title">{t('No active coupons right now')}</span>
          <span className="coupon-empty__note">{t('New codes appear here as soon as they are published.')}</span>
        </div>
      ) : (
        <div className={`coupon-grid${isFetching ? ' coupon-grid--pending' : ''}`}>
          {visible.map(({ coupon }) => (
            <CouponCard key={coupon.id ?? coupon.coupon_name} coupon={coupon} now={now} />
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <nav className="coupon-pager" aria-label={t('Coupon pages')}>
          <Button
            variant="toolbar"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('Previous')}
          </Button>
          <span className="coupon-pager__status">
            {t('Page {{page}} of {{count}}', { page, count: pageCount })}
          </span>
          <Button
            variant="toolbar"
            disabled={page >= pageCount || isFetching}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            {t('Next')}
          </Button>
        </nav>
      )}
    </div>
  );
}
